import { fromJson, toJson, type JsonValue } from '@bufbuild/protobuf';
import {
  ContextFilterSchema,
  type ContextFilterJson
} from '@kentik/schema/gen/ts/kentik/alerting/context/v202602/filter_pb';
import { getContextFieldLabel, getContextOperatorLabel, getContextScopeLabel } from 'app/util/contextFilterLabels';
import {
  CONTEXT_NETWORK_SCOPE_ANY,
  type ContextFieldCapability,
  type ContextFilterCapabilities
} from '@kentik/ui-shared/alertingManager/contextFilterCapabilities';

export const NETWORK_SCOPE_ANY = CONTEXT_NETWORK_SCOPE_ANY;

export const HIDDEN_NETWORK_SCOPES: readonly string[] = ['NETWORK_SCOPE_ADJACENT'];

export interface ContextPredicateRow {
  field: string;
  operator: string;
  not: boolean;
  value: string | string[];
}

export interface ContextClauseValues {
  network_scope: string[];
  predicates: ContextPredicateRow[];
}

export interface ContextConjunctionValues {
  clauses: ContextClauseValues[];
}

export interface ContextFilterValues {
  conjunctions: ContextConjunctionValues[];
}

export class ContextFilterConversionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ContextFilterConversionError';
  }
}

export const newContextPredicate = (): ContextPredicateRow => ({ field: '', operator: '', not: false, value: '' });

export const newContextClause = (): ContextClauseValues => ({
  network_scope: [NETWORK_SCOPE_ANY],
  predicates: [newContextPredicate()]
});

export const newContextConjunction = (): ContextConjunctionValues => ({ clauses: [newContextClause()] });

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toSnakeCase = (name: string): string => name.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const toCamelCase = (name: string): string => name.replace(/_([a-z])/g, (_match, letter) => letter.toUpperCase());

function readPredicate(field: string, raw: unknown): ContextPredicateRow {
  const predicate = isRecord(raw) ? raw : {};
  const [operator, operand] = Object.entries(predicate).find(([key]) => key !== 'not') ?? [];

  let value: string | string[] = '';

  if (isRecord(operand)) {
    value = Array.isArray(operand.values) ? operand.values.map(String) : [];
  } else if (operand !== undefined && operand !== null) {
    value = String(operand);
  }

  return {
    field,
    operator: operator ? toSnakeCase(operator) : '',
    not: predicate.not === true,
    value
  };
}

function readClause(raw: unknown): ContextClauseValues {
  const clause = isRecord(raw) ? raw : {};
  const scope = clause.network_scope ?? clause.networkScope;

  return {
    network_scope: Array.isArray(scope) ? scope.map(String) : [],
    predicates: isRecord(clause.predicates)
      ? Object.entries(clause.predicates).map(([field, value]) => readPredicate(toSnakeCase(field), value))
      : []
  };
}

export function contextFilterToFormValues(raw: unknown): ContextFilterValues {
  const conjunctions = isRecord(raw) && Array.isArray(raw.conjunctions) ? raw.conjunctions : [];

  return {
    conjunctions: conjunctions.map((conjunction) => ({
      clauses: (isRecord(conjunction) && Array.isArray(conjunction.clauses) ? conjunction.clauses : []).map(readClause)
    }))
  };
}

function writePredicate(row: ContextPredicateRow): JsonValue {
  if (!row.field || !row.operator) {
    throw new ContextFilterConversionError('Every condition needs a field and an operator.');
  }

  const predicate: Record<string, JsonValue> = row.not ? { not: true } : {};

  if (Array.isArray(row.value)) {
    if (row.value.length === 0) {
      throw new ContextFilterConversionError(`"${row.field}" needs at least one value.`);
    }

    predicate[row.operator] = { values: row.value.map(String) };

    return predicate;
  }

  const scalar = String(row.value ?? '');

  if (!scalar) {
    throw new ContextFilterConversionError(`"${row.field}" needs a value.`);
  }

  predicate[row.operator] = scalar;

  return predicate;
}

function writeClause(clause: ContextClauseValues): JsonValue {
  if (clause.predicates.length === 0) {
    throw new ContextFilterConversionError('Every condition set needs at least one condition.');
  }

  const predicates: Record<string, JsonValue> = {};

  clause.predicates.forEach((row) => {
    const key = toCamelCase(row.field);

    if (predicates[key] !== undefined) {
      throw new ContextFilterConversionError(`"${row.field}" is used more than once in the same condition set.`);
    }

    predicates[key] = writePredicate(row);
  });

  return { ...(clause.network_scope.length > 0 ? { networkScope: clause.network_scope } : {}), predicates };
}

export function contextFilterFromFormValues(values: ContextFilterValues | undefined): ContextFilterJson | undefined {
  if (!values?.conjunctions.length) {
    return undefined;
  }

  const conjunctions = values.conjunctions.map((conjunction) => {
    if (conjunction.clauses.length === 0) {
      throw new ContextFilterConversionError('Every alternative needs at least one condition set.');
    }

    return { clauses: conjunction.clauses.map(writeClause) };
  });

  const message = fromJson(ContextFilterSchema, { conjunctions });

  return toJson(ContextFilterSchema, message, { useProtoFieldName: true }) as ContextFilterJson;
}

export const getAllowedContextFields = (
  capabilities: ContextFilterCapabilities | undefined,
  whitelist?: readonly string[],
  blacklist?: readonly string[]
): ContextFieldCapability[] =>
  (capabilities?.fields ?? []).filter(
    (field) => (whitelist === undefined || whitelist.includes(field.key)) && !(blacklist ?? []).includes(field.key)
  );

export interface ContextOperatorOption {
  label: string;
  value: string;
  operator: string;
  not: boolean;
}

export function getContextOperatorOptions(
  field: ContextFieldCapability | undefined,
  allowNegation = false
): ContextOperatorOption[] {
  if (!field) {
    return [];
  }

  const { predicateType } = field;

  return field.operators.flatMap(({ key }) => {
    const positive = {
      label: getContextOperatorLabel(key, { predicateType }),
      value: key,
      operator: key,
      not: false
    };

    if (!allowNegation || !field.supportsNegation) {
      return [positive];
    }

    return [
      positive,
      {
        label: getContextOperatorLabel(key, { predicateType, not: true }),
        value: `not_${key}`,
        operator: key,
        not: true
      }
    ];
  });
}

export const getContextOperatorValue = (row: { operator: string; not: boolean }): string =>
  row.operator ? `${row.not ? 'not_' : ''}${row.operator}` : '';

export interface DescribedContextPredicate {
  key: string;
  scope?: string;
  field: string;
  operator: string;
  values: string[];
}

export type ContextValueLabeler = (field: string, value: string) => string | undefined;

export function describeContextFilter(
  raw: unknown,
  { capabilities, resolveValue }: { capabilities?: ContextFilterCapabilities; resolveValue?: ContextValueLabeler } = {}
): DescribedContextPredicate[][] {
  const label = (field: string, value: unknown) => {
    const text = String(value);
    return resolveValue?.(field, text) ?? text;
  };

  return contextFilterToFormValues(raw).conjunctions.map((conjunction, conjunctionIndex) =>
    conjunction.clauses.flatMap((clause, clauseIndex) => {
      const scopes = clause.network_scope.filter((scope) => scope !== NETWORK_SCOPE_ANY);

      return clause.predicates.map((row, rowIndex) => ({
        key: `${conjunctionIndex}.${clauseIndex}.${rowIndex}`,
        scope: scopes.length > 0 ? scopes.map(getContextScopeLabel).join(' or ') : undefined,
        field: getContextFieldLabel(row.field),
        operator: row.operator
          ? getContextOperatorLabel(row.operator, {
              predicateType: capabilities?.fields.find((field) => field.key === row.field)?.predicateType,
              not: row.not
            }).toLowerCase()
          : '?',
        values: [row.value].flat().map((item) => label(row.field, item))
      }));
    })
  );
}

export function collectContextFilterValues(raws: unknown[], field: string): string[] {
  const values = raws.flatMap((raw) =>
    contextFilterToFormValues(raw).conjunctions.flatMap((conjunction) =>
      conjunction.clauses.flatMap((clause) =>
        clause.predicates.filter((row) => row.field === field).flatMap((row) => [row.value].flat())
      )
    )
  );

  return [...new Set(values.map(String).filter(Boolean))];
}
